import { useQuery } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

import { getMinAppVersion } from '@/api/appVersion';
import { decideVersionGate, type VersionGateDecision } from '@/utils/appVersionGate';

export interface AppVersionGate {
  decision: VersionGateDecision;
  /** The backend latest_version driving a 'soft' decision ('' otherwise). */
  latestVersion: string;
}

/**
 * App-version gate (#4 + E3). Native-only: fetches the backend
 * `mandatory_version` / `latest_version` once and decides whether to force an
 * update (blocking) or suggest one (dismissible popin). Fails open — any fetch
 * error or malformed value resolves to 'none', so a backend hiccup can never
 * brick the app.
 */
export function useAppVersionGate(): AppVersionGate {
  const platform = Capacitor.getPlatform();
  const isNative = platform !== 'web';

  const { data } = useQuery({
    queryKey: ['min-app-version'],
    queryFn: getMinAppVersion,
    enabled: isNative,
    retry: false,
    staleTime: 60 * 60 * 1000, // 1h — a startup check, not a hot path
  });

  // deps-security — the NATIVE binary version, not the OTA bundle's
  // (`APP_VERSION`): an old binary running a newer bundle must still be forced.
  // A failed read leaves it undefined ⇒ the decision fails open.
  const { data: nativeInfo } = useQuery({
    queryKey: ['native-app-info'],
    queryFn: () => App.getInfo(),
    enabled: isNative,
    retry: false,
    staleTime: Infinity, // the binary does not change while the app runs
  });

  if (!isNative) return { decision: 'none', latestVersion: '' };
  const decision = decideVersionGate({
    platform,
    nativeVersion: nativeInfo?.version,
    mandatoryVersion: data?.mandatory_version,
    latestVersion: data?.latest_version,
  });
  return { decision, latestVersion: data?.latest_version ?? '' };
}
